import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { useAdjustedBoxes } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAdjustedBoxes'
import { useAliasTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAliasTooltip'
import { useFitToViewport } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useFitToViewport'
import { useShiftDragSelection } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useShiftDragSelection'
import { useViewerOverlayState } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useViewerOverlayState'
import { useZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useZoomControls'
import { PositionedTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/PositionedTooltip'
import {
  ImageContainer,
  MultipageImageContainer,
  StyledImage,
  ViewerContainer,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import { SubjectTimeSeriesTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/SubjectTimeSeriesTooltip'
import {
  Box,
  DocumentViewerHandle,
  TransformState,
  ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ViewerOverlaySvg } from 'features/knowledgeDocumentViewer/components/viewer/shared/ViewerOverlaySvg'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

const MAX_SCALE = 3

export type ImageViewerRolodexOverride = {
  imageUrl: string | undefined
  isLoading: boolean
}

export type ImageViewerProps = ViewerCommonProps & {
  imageUrls: string[]
  boxes: Box[]
  searchHighlights?: SearchHighlightData
  /**
   * A cited region to frame (center + zoom), with the page it belongs to
   * expressed as an index into `imageUrls`. Applied once the imagery is
   * measured and re-applied after each auto-refit while set.
   */
  frameTarget?: { bbox: NormalizedBBox; pageIndex: 0 | 1 }
  onFrameApplied?: () => void
}

export const ImageViewerWithOverlay = forwardRef<
  DocumentViewerHandle,
  ImageViewerProps
>(function ImageViewerWithOverlay(
  {
    imageUrls,
    boxes,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    onResetZoom,
    onZoomStateChange,
    onTransformChange,
    onImageDimensionsChange,
    highlightBoxIds,
    actionMode,
    showTooltips = false,
  },
  ref
) {
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const viewerContainerRef = useRef<HTMLDivElement | null>(null)
  const suppressNextBackgroundClickRef = useRef(false)
  const [minScale, setMinScale] = useState(0.05)
  const [imageDimensions, setImageDimensions] = useState<
    [{ w: number; h: number }, { w: number; h: number }]
  >([
    { w: 0, h: 0 },
    { w: 0, h: 0 },
  ])
  const isMultipage = imageUrls.length > 1

  const { activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem } =
    useKnowledgeDocumentUXContext()
  const instrumentSelection = useInstrumentSelectionOptional()

  const handleBackgroundClick = useCallback(() => {
    if (suppressNextBackgroundClickRef.current) {
      suppressNextBackgroundClickRef.current = false
      return
    }

    instrumentSelection?.clear()
    if (currentSelectedBoxIds.length > 0) {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
    }
  }, [
    activeTabId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    instrumentSelection,
  ])

  const combinedDimensions = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0]
    }

    return {
      w: imageDimensions[0].w + imageDimensions[1].w,
      h: Math.max(imageDimensions[0].h, imageDimensions[1].h),
    }
  }, [imageDimensions, isMultipage])

  const adjustedBoxes = useAdjustedBoxes(
    boxes,
    isMultipage,
    imageDimensions[0].w
  )

  const {
    fillFor,
    getMatchedDatapoints,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    hasTimeSeriesData,
    isLoadingOffPageConnector,
    offPageConnectorDocuments,
    setInternalHighlightedBoxIds,
    strokeFor,
    tooltipBox,
    tooltipLinkedCount,
    tooltipPosition,
    updateTooltipPosition,
  } = useViewerOverlayState({
    adjustedBoxes,
    highlightBoxIds,
    actionMode,
    showTooltips,
    transformRef,
    viewerContainerRef,
    onBoxHover,
  })

  const handleImageLoad = useCallback(
    (index: number) => (event: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget
      setImageDimensions((prev) => {
        const next = [...prev] as [
          { w: number; h: number },
          { w: number; h: number },
        ]
        next[index] = { w: naturalWidth, h: naturalHeight }
        return next
      })
    },
    []
  )

  const allImagesLoaded = useMemo(() => {
    if (!isMultipage) {
      return imageDimensions[0].w > 0 && imageDimensions[0].h > 0
    }

    return (
      imageDimensions[0].w > 0 &&
      imageDimensions[0].h > 0 &&
      imageDimensions[1].w > 0 &&
      imageDimensions[1].h > 0
    )
  }, [imageDimensions, isMultipage])

  const { imgNatural, resetToFit, onFitComplete } = useFitToViewport(
    transformRef,
    setMinScale,
    combinedDimensions,
    isMultipage,
    allImagesLoaded,
    8
  )

  const applyZoom = useCallback(
    (calculateNewScale: (currentScale: number) => number) => {
      if (!transformRef.current) return

      const wrapper = transformRef.current.instance.wrapperComponent
      if (!wrapper) return

      const wrapRect = wrapper.getBoundingClientRect()
      const currentState = transformRef.current.instance.transformState
      const newScale = calculateNewScale(currentState.scale)
      const centerX = wrapRect.width / 2
      const centerY = wrapRect.height / 2
      const scaleDiff = newScale / currentState.scale

      transformRef.current.setTransform(
        centerX - (centerX - currentState.positionX) * scaleDiff,
        centerY - (centerY - currentState.positionY) * scaleDiff,
        newScale,
        200
      )
    },
    []
  )

  const zoomIn = useCallback(() => {
    applyZoom((currentScale) => Math.min(currentScale * 2, 10))
  }, [applyZoom])

  const zoomOut = useCallback(() => {
    applyZoom((currentScale) => Math.max(currentScale / 2, minScale))
  }, [applyZoom, minScale])

  const { zoomToBox, panToRegion, frameRegion, resetZoom } = useZoomControls(
    adjustedBoxes,
    imgNatural,
    transformRef,
    resetToFit,
    onResetZoom,
    zoomIn,
    zoomOut,
    minScale
  )

  const onFrameAppliedRef = useRef(onFrameApplied)
  onFrameAppliedRef.current = onFrameApplied

  // Frame a cited region once the page imagery is measured. Declared after
  // useFitToViewport so on every image load (page change, hi-res swap) the
  // auto-fit effect runs first and this one re-applies the frame on top —
  // the frame owns the final transform for as long as a target is set,
  // while manual pan/zoom (which changes no dependency here) is untouched.
  useEffect(() => {
    if (!frameTarget || !allImagesLoaded) return
    // Mirror SearchHighlightOverlay.getPageLayout: x maps through the target
    // page's own width plus its offset in the combined image; y is already
    // in combined-image space (pages are top-aligned and the overlay scales
    // y by the combined height).
    const firstPageWidth = imageDimensions[0].w
    const onRightPage = isMultipage && frameTarget.pageIndex === 1
    const pageWidth = !isMultipage
      ? combinedDimensions.w
      : onRightPage
        ? combinedDimensions.w - firstPageWidth
        : firstPageWidth
    const xOffset = onRightPage ? firstPageWidth : 0
    const { bbox } = frameTarget
    const framed = frameRegion({
      left: (xOffset + bbox.left * pageWidth) / combinedDimensions.w,
      top: bbox.top,
      width: (bbox.width * pageWidth) / combinedDimensions.w,
      height: bbox.height,
    })
    if (framed) onFrameAppliedRef.current?.()
  }, [
    frameTarget,
    allImagesLoaded,
    imageDimensions,
    isMultipage,
    combinedDimensions,
    frameRegion,
  ])

  // The marquee must also catch alias highlights: fusion-era documents often
  // have no OCR boxes at all, so the highlights are the only instruments.
  const tabData = useDocumentTabDataContextOptional()
  const aliasPseudoBoxes = useMemo<Box[]>(() => {
    if (!instrumentSelection || !tabData || !imgNatural.w) return []
    const { aliasHighlights, pageNumber } = tabData
    const firstPageWidth = imageDimensions[0].w
    return aliasHighlights
      .filter(
        (highlight) =>
          highlight.page === pageNumber ||
          (isMultipage && highlight.page === pageNumber + 1)
      )
      .map((highlight, idx) => {
        const onRightPage = isMultipage && highlight.page > pageNumber
        const pageWidth = !isMultipage
          ? imgNatural.w
          : onRightPage
            ? imgNatural.w - firstPageWidth
            : firstPageWidth
        const xOffset = onRightPage ? firstPageWidth : 0
        return {
          id: `alias:${highlight.subjectUid}:${idx}`,
          confirmed: true,
          x: xOffset + highlight.bbox.left * pageWidth,
          y: highlight.bbox.top * imgNatural.h,
          w: highlight.bbox.width * pageWidth,
          h: highlight.bbox.height * imgNatural.h,
          label: highlight.label,
        }
      })
  }, [instrumentSelection, tabData, imgNatural, imageDimensions, isMultipage])

  const { handleAliasHover, aliasTooltipProps } = useAliasTooltip({
    showTooltips,
    containerRef: viewerContainerRef,
  })

  const marqueeBoxes = useMemo(
    () =>
      aliasPseudoBoxes.length > 0
        ? [...adjustedBoxes, ...aliasPseudoBoxes]
        : adjustedBoxes,
    [adjustedBoxes, aliasPseudoBoxes]
  )

  const {
    isShiftPressed,
    isDragging,
    selectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useShiftDragSelection({
    svgRef,
    boxes: marqueeBoxes,
    onSelectionComplete: (selectedIds) => {
      suppressNextBackgroundClickRef.current = true
      const selectedIdSet = new Set(selectedIds)
      onMultipleBoxSelect?.(
        selectedIds,
        marqueeBoxes.filter((box) => selectedIdSet.has(box.id))
      )
    },
    imageNaturalDimensions: imgNatural,
  })

  const checkAndNotifyZoomState = useCallback(() => {
    if (!transformRef.current || !onZoomStateChange) return

    const currentScale = transformRef.current.instance.transformState.scale
    const epsilon = 0.001

    onZoomStateChange({
      isAtMinZoom: Math.abs(currentScale - minScale) < epsilon,
      isAtMaxZoom:
        Math.abs(currentScale - MAX_SCALE) < epsilon ||
        currentScale >= MAX_SCALE,
    })
  }, [minScale, onZoomStateChange])

  useEffect(() => {
    checkAndNotifyZoomState()
  }, [checkAndNotifyZoomState])

  useEffect(() => {
    if (!imgNatural.w || !imgNatural.h) {
      return
    }

    onImageDimensionsChange?.(imgNatural)
  }, [imgNatural, onImageDimensionsChange])

  const getCurrentTransformState = useCallback((): TransformState | null => {
    const currentState = transformRef.current?.instance.transformState
    if (!currentState) {
      return null
    }

    return {
      scale: currentState.scale,
      positionX: currentState.positionX,
      positionY: currentState.positionY,
    }
  }, [])

  const handleTransformed = useCallback(() => {
    checkAndNotifyZoomState()
    updateTooltipPosition()

    const currentTransformState = getCurrentTransformState()
    if (currentTransformState) {
      onTransformChange?.(currentTransformState)
    }
  }, [
    checkAndNotifyZoomState,
    getCurrentTransformState,
    onTransformChange,
    updateTooltipPosition,
  ])

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox,
      panToRegion,
      setHighlightedBoxIds: setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
      setTransform: (posX, posY, nextScale, animTime = 0) => {
        transformRef.current?.setTransform(posX, posY, nextScale, animTime)
      },
      getViewportInfo: () => {
        const wrapper = transformRef.current?.instance.wrapperComponent
        if (!wrapper) {
          return null
        }

        return {
          wrapperWidth: wrapper.clientWidth,
          wrapperHeight: wrapper.clientHeight,
        }
      },
      getImageNaturalDimensions: () => imgNatural,
    }),
    [
      imgNatural,
      zoomToBox,
      panToRegion,
      setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
    ]
  )

  return (
    <ViewerContainer
      ref={viewerContainerRef}
      style={{ cursor: isShiftPressed ? 'crosshair' : 'default' }}>
      <TransformWrapper
        ref={transformRef}
        minScale={minScale}
        maxScale={10}
        limitToBounds
        wheel={{ step: 0.5 }}
        doubleClick={{ disabled: false, mode: 'zoomIn' }}
        pinch={{ disabled: false }}
        alignmentAnimation={{ sizeX: 100, sizeY: 100 }}
        onTransformed={handleTransformed}>
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ display: 'inline-block' }}>
          <ImageContainer
            $opacity={onFitComplete ? 1 : 0}
            $width={imgNatural ? `${imgNatural.w}px` : 'auto'}
            $height={imgNatural ? `${imgNatural.h}px` : 'auto'}>
            {isMultipage ? (
              <MultipageImageContainer>
                {imageUrls.map((url, index) => (
                  <StyledImage
                    key={url}
                    src={url}
                    alt={`diagram page ${index + 1}`}
                    onLoad={handleImageLoad(index)}
                    draggable={false}
                  />
                ))}
              </MultipageImageContainer>
            ) : (
              <StyledImage
                src={imageUrls[0]}
                alt='diagram'
                onLoad={handleImageLoad(0)}
                draggable={false}
              />
            )}

            {!!imgNatural && (
              <ViewerOverlaySvg
                actionMode={actionMode}
                boxes={adjustedBoxes}
                disableBackgroundClick={isShiftPressed}
                fillFor={fillFor}
                firstPageWidth={imageDimensions[0].w}
                hasTimeSeriesData={hasTimeSeriesData}
                imageNatural={imgNatural}
                isDragging={isDragging}
                isMultipage={isMultipage}
                searchHighlights={searchHighlights}
                onBackgroundClick={handleBackgroundClick}
                onBoxAuxClick={onBoxAuxClick}
                onBoxClick={onBoxClick}
                onBoxContextMenu={onBoxContextMenu}
                onBoxMouseEnter={handleBoxMouseEnter}
                onBoxMouseLeave={handleBoxMouseLeave}
                onAliasClick={onAliasClick}
                onAliasHover={handleAliasHover}
                onDocReferenceClick={onDocReferenceClick}
                onDocReferenceHover={onDocReferenceHover}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                selectionBox={selectionBox}
                strokeFor={strokeFor}
                svgRef={svgRef}
              />
            )}
          </ImageContainer>
        </TransformComponent>
      </TransformWrapper>
      {showTooltips && tooltipBox && tooltipPosition && (
        <PositionedTooltip
          tooltipBox={tooltipBox}
          tooltipPosition={tooltipPosition}
          viewportWidth={viewerContainerRef.current?.clientWidth ?? 1200}
          viewportHeight={viewerContainerRef.current?.clientHeight ?? 800}
          hasTimeSeriesData={hasTimeSeriesData}
          getMatchedDatapoints={getMatchedDatapoints}
          offPageConnectorDocuments={offPageConnectorDocuments}
          isLoadingOffPageConnector={isLoadingOffPageConnector}
          tooltipLinkedCount={tooltipLinkedCount}
        />
      )}
      {aliasTooltipProps && <SubjectTimeSeriesTooltip {...aliasTooltipProps} />}
    </ViewerContainer>
  )
})

export default ImageViewerWithOverlay
